import React from 'react';
import { BaseComponent, IStateBase } from 'components/BaseComponent';
import { pixelService } from 'services/PixelService';
import LegacyPixel from 'components/Pixel/LegacyPixel';
import { WithStyles } from 'shared/decorators/withStyles';
import { IS_DEVELOPMENT, PIXEL_URL } from 'settings';
import * as Sentry from '@sentry/browser';

interface IState extends IStateBase {
  hasLegacy: boolean;
  addAffiliatePixel: string;
  urlAfiliate: string;
  urlProducer: string;
}

type IPixelIframe = {
  pixelId: string;
  iframeType: string;
};

const MAX_RETRIES = 3;
const RETRY_TIMEOUT = 5000;

@WithStyles(() => ({
  component: {
    position: 'absolute'
  }
}))
export default class Pixel extends BaseComponent<IState> {
  retryCount: Record<string, number> = {};
  timeoutRefs: Record<string, ReturnType<typeof setTimeout>> = {};
  trustedIframeOrigins = [PIXEL_URL];

  state = {
    hasLegacy: true,
    addAffiliatePixel: '',
    urlProducer: '',
    urlAfiliate: ''
  };

  componentDidMount() {
    pixelService
      .init()
      .bindComponent(this)
      .filter((pixel) => {
        /*
          hasLegacy: Should be removed in not too distance future
        */
        const scripts = Object.values(pixel.scripts).filter((value) => value !== null);

        return scripts.length !== 0 || !!pixel.urlProducer || !!pixel.urlAfiliate;
      })
      .subscribe((pixel) => {
        const { scripts, urlProducer = '', urlAfiliate = '' } = pixel;
        this.setState({ urlProducer, urlAfiliate, hasLegacy: false });

        if (!scripts) {
          return;
        }

        if (scripts.analytics) {
          this.scriptPixel('ANALYTICS_PIXEL_SCRIPT', scripts.analytics, pixel?.path);
        }

        if (scripts.tagmanager) {
          this.scriptPixel('TAGMANAGER_PIXEL_SCRIPT', scripts.tagmanager, pixel?.path);
        }

        if (scripts.hubspot) {
          this.scriptPixel('HUBSPOT_PIXEL_SCRIPT', scripts.hubspot, pixel?.path);
        }

        if (scripts?.analyticsG4) {
          this.scriptPixel('ANALYTICSG4_PIXEL_SCRIPT', scripts.analyticsG4, pixel?.path);
        }

        if (scripts?.tiktok) {
          this.scriptPixel('TIKTOK_PIXEL_SCRIPT', scripts.tiktok, pixel?.path);
        }
      });

    pixelService
      .setAffiliationCookie()
      .bindComponent(this)
      .subscribe((addAffiliatePixel) => {
        this.setState({ addAffiliatePixel });
      });

    window.addEventListener('message', this.onIframeMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onIframeMessage);
  }

  scriptPixel = (id: string, script: any, path?: string) => {
    if (!path.includes('bankslip-view')) {
      this.removeScriptsBody(`${id}_BODY`);
    }

    if (script.head) {
      let headElementScript = document.getElementById(`${id}_HEAD`);

      if (!headElementScript) {
        headElementScript = document.createElement('script');
        headElementScript.setAttribute('id', `${id}_HEAD`);
      }

      headElementScript.innerHTML = script.head;
      document.head.appendChild(headElementScript);
    }

    const bodyId = `${id}_BODY_${path}`;
    if (!!document.getElementById(bodyId)) {
      return;
    }

    const bodyElementScript = document.createElement('script');

    bodyElementScript.setAttribute('id', bodyId);
    bodyElementScript.setAttribute('data-type', `${id}_BODY`);

    bodyElementScript.innerHTML = script.body;
    document.body.appendChild(bodyElementScript);
  };

  onIframeMessage = (event: MessageEvent) => {
    const isTrustedOrigin = this.trustedIframeOrigins.some((origin) => event.origin.includes(origin));

    const isPixelIframeMessage = event.data && typeof event.data === 'object' && event.data?.type === 'iframe-ready';

    if (!isPixelIframeMessage) {
      return;
    }

    const { pixelId } = event.data;

    if (!isTrustedOrigin) {
      const error = new Error(`The pixel origin URL is invalid (${event.origin})`);

      Sentry.withScope((scope) => {
        scope.setTag('pixelId', pixelId);
        scope.setContext('event', { event });
        Sentry.captureException(error);
      });

      return;
    }

    this.clearRetryTimeout(pixelId);
  };

  removeScriptsBody = (dataType: string) => {
    document.querySelectorAll(`[data-type=${dataType}]`).forEach((elem) => {
      elem.remove();
    });
  };

  handleIframeTimeout = ({ pixelId, iframeType }: IPixelIframe) => {
    this.retryCount[iframeType] = (this.retryCount[iframeType] || 0) + 1;

    const iframe = document.getElementById(`pixel-script-${iframeType}`) as HTMLIFrameElement;

    if (!iframe) {
      return;
    }

    if (this.retryCount[iframeType] <= MAX_RETRIES) {
      IS_DEVELOPMENT && console.warn(`[Pixel] ${pixelId} (${iframeType}) failed to load iframe. Retry #${this.retryCount[iframeType]}`);

      iframe.src = iframe.src; // force iframe reload
      this.setRetryTimeout({ pixelId, iframeType });
      return;
    }

    const error = new Error(`Failed to load Pixel iframe. Pixel ID: ${pixelId} (${iframeType})`);
    Sentry.captureException(error);
    IS_DEVELOPMENT && console.error(`[Pixel] ${error.message}`);
  };

  setRetryTimeout = ({ pixelId, iframeType }: IPixelIframe) => {
    this.clearRetryTimeout(pixelId);
    this.timeoutRefs[pixelId] = setTimeout(() => this.handleIframeTimeout({ pixelId, iframeType }), RETRY_TIMEOUT);
  };

  clearRetryTimeout = (pixelId: string) => {
    if (!this.timeoutRefs[pixelId]) {
      return;
    }

    clearTimeout(this.timeoutRefs[pixelId]);
    delete this.timeoutRefs[pixelId];
  };

  iframePixel = (urlIFrame: string, type: string) => {
    if (!urlIFrame) {
      return null;
    }

    const url = new URL(urlIFrame);
    const origin = url.origin;
    const pixelId = url.pathname.split('/').filter(Boolean).pop();

    if (!this.trustedIframeOrigins.includes(origin)) {
      this.trustedIframeOrigins.push(origin);
    }

    if (pixelId) {
      this.setRetryTimeout({ pixelId, iframeType: type });
    }

    return <iframe name="pixelB" style={{ position: 'fixed' }} id={`pixel-script-${type}`} src={urlIFrame} width="1" height="1" frameBorder="0" />;
  };

  render() {
    const { hasLegacy, addAffiliatePixel, urlProducer, urlAfiliate } = this.state;

    return (
      <div className={this.props.classes.component}>
        {!hasLegacy ? (
          <React.Fragment>
            {addAffiliatePixel && (
              <iframe name="pixelA" style={{ position: 'fixed' }} id="pixel-mark-affiliate" src={addAffiliatePixel} width="1" height="1" />
            )}
            {this.iframePixel(urlProducer, 'producer')}
            {this.iframePixel(urlAfiliate, 'afiliate')}
          </React.Fragment>
        ) : (
          <LegacyPixel />
        )}
      </div>
    );
  }
}
